import {
  BID_OFFER_MATCH_ALGO_OPTIONS,
  CLOUD_COVERAGE_OPTIONS,
  CURRENCY_OPTIONS,
  GRID_FEE_TYPE_OPTIONS,
  SPOT_MARKET_TYPE_OPTIONS,
  SWITCHER_ICON_SIZES,
} from 'src/constants/application';
import {
  TCommunitySettingsFields,
  TFieldsUnionWithValue,
  TValuesByFieldName,
} from 'src/utils/assetsFields/assetsFields.types';
import { TValidators, rules } from 'src/utils/validation';
import { configType, isLibrary } from 'src/mocks/configurationSettings';

import { BACKEND_DATE_FORMATS } from 'src/utils/UTCMoment';
import { EFormVariant } from 'src/typings/base-types';
import { TConfigurationCharacteristic } from 'src/typings/configuration.types';
import { getAllFieldTemplatesWithValuesForAsset } from 'src/utils/assetsFields/fieldTemplatesWithValues';

export const fieldTemplates = ({
  values,
  configurationCharacteristic,
}: {
  values: Omit<TCommunitySettingsFields, 'startEndDate'> &
    Pick<
      NonNullable<TValuesByFieldName>,
      'gridFeeEnabled' | 'gridFeeConstant' | 'gridFeePercentage'
    >;
  configurationCharacteristic: TConfigurationCharacteristic;
}): TFieldsUnionWithValue[] => {
  const communityAssetFields = getAllFieldTemplatesWithValuesForAsset({
    type: 'Area',
    settingsData: values,
    isLibrary,
    values: {
      gridFeeEnabled: values.gridFeeEnabled,
      gridFeePercentage: values.gridFeePercentage || undefined,
      gridFeeConstant: values.gridFeeConstant || undefined,
    },
    configType,
    configurationCharacteristic,
    includeAll: true,
  }).filter((f) => ['gridFeeEnabled', 'gridFeePercentage', 'gridFeeConstant'].includes(f.name));

  const output: TFieldsUnionWithValue[] = [
    {
      name: 'name',
      type: 'text',
      label: 'Name your community',
      formView: EFormVariant.Express,
      value: values.name,
    },
    {
      name: 'currency',
      type: 'enum',
      label: 'Community Currency',
      options: CURRENCY_OPTIONS,
      formView: EFormVariant.Express,
      value: values.currency,
    },
    {
      name: 'startEndDate',
      type: 'dateRange',
      valueFormat: BACKEND_DATE_FORMATS.SETTINGS_DATA,
      formView: EFormVariant.Express,
      value: {
        startDate: values.startDate,
        endDate: values.endDate,
      },
    },
    {
      name: 'locationVisible',
      type: 'switcher',
      options: [
        { icon: 'close', iconSize: SWITCHER_ICON_SIZES.close, value: false },
        { icon: 'check-mark', iconSize: SWITCHER_ICON_SIZES.tick, value: true },
      ],
      value: values.locationVisible,
      label: 'Show my community on map',
      formView: EFormVariant.Express,
    },
    {
      name: 'description',
      type: 'textarea',
      label: 'Description',
      formView: EFormVariant.Advanced,
      value: values.description,
    },
    {
      name: 'cloudCoverage',
      type: 'enum',
      label: 'Solar profile',
      options: CLOUD_COVERAGE_OPTIONS,
      formView: EFormVariant.Advanced,
      value: values.cloudCoverage,
    },
    {
      name: 'pvUserProfile',
      type: 'file',
      label: 'Upload profile',
      formView: EFormVariant.Advanced,
      value: values.pvUserProfile,
    },
    {
      name: 'spotMarketType',
      type: 'enum',
      label: 'Spot market type',
      options: SPOT_MARKET_TYPE_OPTIONS,
      formView: EFormVariant.Advanced,
      value: values.spotMarketType,
    },
    {
      name: 'bidOfferMatchAlgo',
      type: 'enum',
      label: 'Bid offer match algorithm',
      options: BID_OFFER_MATCH_ALGO_OPTIONS,
      formView: EFormVariant.Advanced,
      value: values.bidOfferMatchAlgo,
    },
    {
      name: 'marketCount',
      type: 'number',
      label: 'Number of spot markets',
      formView: EFormVariant.Advanced,
      value: values.marketCount,
    },
    {
      name: 'slotLengthMinutes',
      type: 'number',
      label: 'Length of spot market',
      unit: 'min',
      formView: EFormVariant.Advanced,
      value: values.slotLengthMinutes,
    },
    {
      name: 'tickLengthSeconds',
      type: 'number',
      label: 'Tick length',
      unit: 'sec',
      formView: EFormVariant.Advanced,
      value: values.tickLengthSeconds,
    },
    {
      name: 'gridFeeType',
      type: 'enum',
      label: 'Grid Fees',
      options: GRID_FEE_TYPE_OPTIONS,
      formView: EFormVariant.Advanced,
      value: values.gridFeeType,
    },
    ...communityAssetFields,
    {
      name: 'slotLengthRealtimeSeconds',
      type: 'number',
      label: 'Market slot real time duration',
      unit: 'sec',
      formView: EFormVariant.Advanced,
      value: values.slotLengthRealtimeSeconds,
    },
    // {
    //   name: 'transformerCapacityEnabled',
    //   type: 'switcher',
    //   options: [
    //     { icon: 'check-mark', iconSize: SWITCHER_ICON_SIZES.tick, value: true },
    //     { icon: 'close', iconSize: SWITCHER_ICON_SIZES.close, value: false },
    //   ],
    //   formView: EFormVariant.Advanced,
    //   label: 'Transformer capacity',
    //   value: values.transformerCapacityEnabled,
    // },
    // {
    //   name: 'baselinePeakEnergyEnabled',
    //   type: 'switcher',
    //   options: [
    //     { icon: 'check-mark', iconSize: SWITCHER_ICON_SIZES.tick, value: true },
    //     { icon: 'close', iconSize: SWITCHER_ICON_SIZES.close, value: false },
    //   ],
    //   formView: EFormVariant.Advanced,
    //   label: 'Energy peak percentage',
    //   value: values.baselinePeakEnergyEnabled,
    // },
  ];

  return output;
};

export const validators = ({
  usedProjectNames,
  currentProjectName,
}: {
  usedProjectNames: string[];
  currentProjectName: string;
}): TValidators<keyof TCommunitySettingsFields> => ({
  name: [
    ...((n) => [
      rules.required(n),
      rules.isNameUnique(usedProjectNames, currentProjectName),
      rules.maxLength(n, 50),
    ])('Name'),
  ],
  marketCount: [...((n) => [rules.integer(n)])('Number of spot markets')],
  slotLengthMinutes: [
    ...((n) => [rules.integer(n), rules.range(n, 2, 60)])('Length of spot market'),
  ],
  tickLengthSeconds: [...((n) => [rules.integer(n), rules.range(n, 1, 90)])('Tick length')],
  bidOfferMatchAlgo: [...((n) => [rules.required(n)])('Bid offer match algorithm')],
});
